import React, { useState, useEffect } from "react";
import {
  fetchCapabilities,
  submitCapabilities,
  fetchSelectedCapabilities,
} from "../../services/capabilitiesApi";
import { useAuth } from "../../auth/AuthContext";
import {
  isLeafCapability,
  sameAssessmentUnit,
  selectableUnitCount,
} from "../../utils/assessmentUnitUtils";

const Cap = ({ user: userProp }) => {
  const basename = process.env.PUBLIC_URL || "";
  const { user: authUser } = useAuth();
  const user = userProp || authUser;

  const [visibleSection, setVisibleSection] = useState(null);
  const [capabilities, setCapabilities] = useState([]);
  const [selectedCapabilities, setSelectedCapabilities] = useState([]);
  const [selectedCounts, setSelectedCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [requestStatus, setRequestStatus] = useState("Not Requested");
  const [capabilitiesState, setCapabilitiesState] = useState({
    allSubmitted: false
  });
  const [isActive, setIsActive] = useState(false);

  // Get projectId from localStorage
  useEffect(() => {
    const storedProjectId = localStorage.getItem("projectId");
    if (storedProjectId) {
      setProjectId(parseInt(storedProjectId, 10));
    }
  }, []);

  // Set userId from auth context or passed user prop
  useEffect(() => {
    if (user?.userId) {
      setUserId(user.userId);
    }
  }, [user]);

  // Fetch capabilities, preselected options, and request status when projectId or userId changes.
  useEffect(() => {
    if (!projectId || !userId) return;

    const fetchData = async () => {
      try {
        // Get all capabilities for the project
        const capabilitiesResponse = await fetchCapabilities(projectId);
        setCapabilities(capabilitiesResponse.data);

        // Get previously selected capabilities for the current user and project
        const selectedResponse = await fetchSelectedCapabilities({ userId, projectId });
        const preselected =
          selectedResponse?.data?.selectedCapabilities?.flatMap((cap) =>
            cap.selectedSubCapabilities.map((subCap) => ({
              capabilityId: cap.capabilityId,
              subCapabilityId: subCap.subCapabilityId,
              state: subCap.state || "3",
              isNewLocked: subCap.isNewLocked ?? false,
              isApproved: subCap.state === "4",
              isNewlySelected: false,
            }))
          ) || [];
        setSelectedCapabilities(preselected);

        // Determine if all capabilities have been submitted
        const allSubmitted = preselected.length > 0 && preselected.every((cap) => cap.state === "4");

        setCapabilitiesState({
          allSubmitted
        });

        // Build counts for selected sub-capabilities per capability
        setSelectedCounts(
          preselected.reduce((counts, item) => {
            counts[item.capabilityId] = (counts[item.capabilityId] || 0) + 1;
            return counts;
          }, {})
        );
      } catch (error) {
        console.error("Error fetching capabilities:", error);
        setError("Failed to load capabilities data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId, userId]);

  const toggleVisibility = (sectionId) => {
    setVisibleSection((prev) => (prev === sectionId ? null : sectionId));
    setSelectedCard(sectionId);
  };

  const handleCheckboxChange = (e, capabilityId, subCapabilityId) => {
    const isChecked = e.target.checked;

    if (capabilitiesState.userCanAddNewCapabilities) {
      setSelectedCapabilities((prev) => {
        const existingSelection = prev.find(
          (item) => sameAssessmentUnit(item.capabilityId, item.subCapabilityId, capabilityId, subCapabilityId)
        );

        if (existingSelection?.isApproved || existingSelection?.state === "4") {
          return prev;
        }

        if (isChecked) {
          return [
            ...prev,
            {
              capabilityId,
              subCapabilityId,
              state: "3",
              isNewLocked: false,
              isApproved: false,
              isNewlySelected: true,
            },
          ];
        } else {
          return prev.filter(
            (item) =>
              !(sameAssessmentUnit(item.capabilityId, item.subCapabilityId, capabilityId, subCapabilityId) && item.isNewlySelected)
          );
        }
      });

      setSelectedCounts((prevCounts) => {
        const updatedCounts = { ...prevCounts };
        const currentSelectedCount = updatedCounts[capabilityId] || 0;
        updatedCounts[capabilityId] = isChecked
          ? currentSelectedCount + 1
          : Math.max(currentSelectedCount - 1, 0);
        return updatedCounts;
      });
    }
  };

  const handleSubmit = async () => {
    try {
      if (!projectId) {
        alert("Project ID is not available. Cannot submit.");
        return;
      }

      const newSelections = selectedCapabilities.filter((cap) => cap.isNewlySelected);
      if (newSelections.length === 0) {
        alert("No new capabilities selected.");
        return;
      }

      const updatedCapabilities = selectedCapabilities.map((cap) =>
        cap.isNewlySelected ? { ...cap, state: "3" } : cap
      );

      const payload = {
        userId,
        projectId,
        selectedCapabilities: updatedCapabilities,
      };

      await submitCapabilities(payload);

      setSelectedCapabilities(updatedCapabilities);

      // Optionally, re-fetch the selected capabilities here.
      await fetchSelectedCapabilities({ userId, projectId });
    } catch (error) {
      console.error("Error submitting capabilities:", error);
    }
  };

  return (
    <div className="cap-frame-1000005381">
      <div className="cap-frame-1000005388">
        <div className="cap-next-parents">
          <div className="cap-frame-content">
            <div className="cap-message-section">
              <div className="cap-frame-parent4">
                <div className="cap-capabilities-list">
                  {capabilities.map((capability) => {
                    const leaf = isLeafCapability(capability);
                    const leafSelected = selectedCapabilities.some(
                      (item) =>
                        sameAssessmentUnit(
                          item.capabilityId,
                          item.subCapabilityId,
                          capability.id,
                          null
                        )
                    );

                    return (
                    <div className="cap-capability-row" key={capability.id}>
                      <div
                        className={`cap-frame-parent5 ${selectedCard === capability.id || (leaf && leafSelected) ? "highlight" : ""}`}
                        onClick={() => {
                          if (leaf) return;
                          toggleVisibility(capability.id);
                        }}
                      >
                        {leaf && (
                          <div className="cap-checkbox-input">
                            <input
                              type="checkbox"
                              className="cap-real-checkbox"
                              checked={leafSelected}
                              disabled
                              readOnly
                              aria-label={capability.capabilityName}
                            />
                            <div className="cap-bg"></div>
                          </div>
                        )}
                        <div className="cap-strategic-management-parent">
                          <div className="cap-strategic-management">{capability.capabilityName}</div>
                          <div className="cap-focuses-on-planning">{capability.capabilityDescription}</div>
                        </div>
                        <div className="cap-frame-wrapper2">
                          <div className="cap-selected-parent">
                            <div className="cap-selected">
                              {`${selectedCounts[capability.id] || 0}/${selectableUnitCount(capability)} selected`}
                            </div>
                            {!leaf && (
                              <img
                                className="cap-frame-item"
                                alt="Toggle Checkboxes"
                                src={`${basename}/images/plus.webp`}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                      {!leaf && visibleSection === capability.id && (
                        <div className="cap-frame-wrapper12">
                          <div className="cap-frame-parent6">
                            <div className="cap-frame-1000005422">
                              {(capability.subCapabilities ?? []).map((subCapability) => {
                                const isPreviouslySelected = selectedCapabilities.some(
                                  (item) =>
                                    sameAssessmentUnit(
                                      item.capabilityId,
                                      item.subCapabilityId,
                                      capability.id,
                                      subCapability.subId
                                    )
                                );
                                return (
                                  <div className="cap-checkbox-parent" key={subCapability.subId}>
                                    <div className="cap-checkbox">
                                      <div className="cap-checkbox-input">
                                        <input
                                          type="checkbox"
                                          className="cap-real-checkbox"
                                          value={subCapability.subId}
                                          onChange={(e) =>
                                            handleCheckboxChange(e, capability.id, subCapability.subId)
                                          }
                                          checked={isPreviouslySelected}
                                          disabled={'disabled'}
                                        />
                                        <div className="cap-bg"></div>
                                      </div>
                                      <div className="cap-strategic-management-parent">
                                        <div className="cap-data-ai">{subCapability.subCapabilityName}</div>
                                        <div className="Sub-cap-data-ai">{subCapability.subCapabilityDescription}</div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cap;
